// Server-side chat identity + its wire-form conversions.
//
// A persisted chat carries a server UUID minted once at creation (or backfill)
// and stored on the IDB record alongside the local numeric id. The local
// numeric id + the id fence (chatIds.js) are UNTOUCHED — this is an additive
// field that maps a per-browser chat onto its server row.
//
// Wire-form is deliberately split across the chat-persistence backend surface:
//   • the gRPC-gateway RPCs — the reads (ListChats / GetChat / ListChatTurns),
//     DeleteChat, AND the transcoded boundary write SyncChat (POST
//     /v1/chats:sync) — take the id as a proto `bytes` field, which is BASE64 in
//     the gateway JSON;
//   • the plain-HTTP blob routes (PUT / GET /v1/chat-blobs) take a HYPHENATED
//     UUID string in the query (serde `Uuid`), for both chat_id and blob_id.
// So the FE holds the hyphenated form and converts to base64 only at the RPC
// boundary (the blob routes need no conversion). These helpers are the
// sanctioned, scoped conversion for the chat id — the same posture as
// inventoryRows.js's endpoint-id bridge (see dashboard/AGENTS.md → "IDs on the
// wire"). Do NOT generalize them into an app-wide UUID utility.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Mint a fresh server chat UUID (hyphenated, lowercase). Uses the platform
// crypto UUID generator; falls back to a manual v4 assembly only where
// `crypto.randomUUID` is missing (very old/embedded webviews) so the dashboard
// never hard-depends on it.
export function mintServerChatId() {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
    } catch { /* fall through to manual assembly */ }
    return manualUuidV4();
}

export function isServerChatId(value) {
    return typeof value === "string" && UUID_RE.test(value);
}

// Hyphenated UUID → base64 of its 16 raw bytes (proto `bytes` wire form).
export function chatIdToWireBytes(uuid) {
    if (!isServerChatId(uuid)) {
        throw new Error("chatIdToWireBytes: not a UUID");
    }
    const hex = uuid.replace(/-/g, "");
    let binary = "";
    for (let i = 0; i < 16; i++) {
        binary += String.fromCharCode(parseInt(hex.slice(i * 2, i * 2 + 2), 16));
    }
    return btoa(binary);
}

// base64 of 16 raw bytes (proto `bytes` wire form) → hyphenated UUID. Accepts
// both standard and URL-safe base64 (the gateway may emit either). Returns
// null on anything that isn't 16 decodable bytes so a malformed server row
// can't crash a restore.
export function wireBytesToChatId(b64) {
    if (typeof b64 !== "string" || !b64) return null;
    const standard = b64.replace(/-/g, "+").replace(/_/g, "/");
    let binary;
    try {
        binary = atob(standard);
    } catch {
        return null;
    }
    if (binary.length !== 16) return null;
    let hex = "";
    for (let i = 0; i < 16; i++) {
        hex += binary.charCodeAt(i).toString(16).padStart(2, "0");
    }
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function manualUuidV4() {
    const bytes = new Uint8Array(16);
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
        crypto.getRandomValues(bytes);
    } else {
        // Last-resort non-crypto fill; the id only needs to be collision-free
        // per browser, and a platform this old is not a real deployment target.
        for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    let hex = "";
    for (let i = 0; i < 16; i++) hex += bytes[i].toString(16).padStart(2, "0");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
